'use client';
import { useState, useRef } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import Link from 'next/link';
import { ArrowLeft, Copy, Download, Check, FileUp, Trash2 } from 'lucide-react';

export default function DuplicateRemoverPage() {
  const [csvInput, setCsvInput] = useState('');
  const [csvOutput, setCsvOutput] = useState('');
  const [stats, setStats] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const removeDuplicates = () => {
    try {
      setError('');
      if (!csvInput.trim()) {
        setError('Please enter CSV data');
        return;
      }

      const lines = csvInput.trim().split('\n');
      if (lines.length < 1) {
        setError('CSV is empty');
        return;
      }

      const header = lines[0];
      const dataRows = lines.slice(1);
      const uniqueRows = Array.from(new Set(dataRows));
      const removedCount = dataRows.length - uniqueRows.length;

      setCsvOutput([header, ...uniqueRows].join('\n'));
      setStats(`Removed ${removedCount} duplicate rows. ${uniqueRows.length} unique rows remaining.`);
    } catch (err) {
      setError('Error removing duplicates. Please check your input format.');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(csvOutput);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadCsv = () => {
    const blob = new Blob([csvOutput], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cleaned_data.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (file: File) => {
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.txt')) {
      setError('Please upload a CSV or TXT file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setCsvInput(text);
    };
    reader.onerror = () => {
      setError('Error reading file');
    };
    reader.readAsText(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="marketing-theme tool-page">

      <Navbar />

      <main className="tool-page-main flex flex-col items-center">
        <div className="w-full max-w-5xl">
          <Link 
            href="/tools" 
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tools
          </Link>

          <div className="text-center mb-8">
              <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                CSV Duplicate Remover
              </h1>
            <p className="text-lg text-muted-foreground">
              Instantly find and remove duplicate rows from your CSV data
            </p>
          </div>

          {/* File Upload Area */}
          <div className="mb-6">
            <label className="text-sm font-medium mb-2 block">Upload CSV File</label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={triggerFileInput}
              className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-all ${
                isDragging 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                  : 'border-gray-300 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-600'
              }`}
            >
              <FileUp className={`w-12 h-12 mx-auto mb-4 ${
                isDragging ? 'text-blue-500' : 'text-gray-400'
              }`} />
              <p className="text-lg font-medium mb-2">
                {isDragging ? 'Drop your file here' : 'Click to upload or drag and drop'}
              </p>
              <p className="text-sm text-muted-foreground">
                Supports .csv and .txt files
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                onChange={handleFileInputChange}
                className="hidden"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Section */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">CSV Input</label>
                <Textarea
                  placeholder="Name,Email&#10;John,john@example.com&#10;John,john@example.com&#10;Jane,jane@example.com"
                  value={csvInput}
                  onChange={(e) => setCsvInput(e.target.value)}
                  className="min-h-[200px] font-mono text-sm"
                />
              </div>
              <Button 
                onClick={removeDuplicates} 
                className="w-full"
                size="lg"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove Duplicates
              </Button>
              {error && (
                <div className="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                  {error}
                </div>
              )}
              {stats && (
                <div className="text-blue-600 text-sm bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                  {stats}
                </div>
              )}
            </div>

            {/* Output Section */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Cleaned Output</label>
                <Textarea
                  value={csvOutput}
                  readOnly
                  placeholder="Your cleaned data will appear here..."
                  className="min-h-[200px] font-mono text-sm bg-muted"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={copyToClipboard} 
                  className="flex-1"
                  variant="outline"
                  disabled={!csvOutput}
                >
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
                <Button 
                  onClick={downloadCsv} 
                  className="flex-1"
                  variant="outline"
                  disabled={!csvOutput}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <section className="max-w-4xl mx-auto mt-16 mb-12 p-6 bg-card rounded-lg border border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold mb-4">Duplicate Remover FAQs</h2>
        <div className="space-y-6">
          <div><p className="font-semibold">1. How does it identify duplicates?</p><p className="text-muted-foreground">It compares entire rows. If two rows have identical content in all columns, one is removed.</p></div>
          <div><p className="font-semibold">2. Does it keep the first or last occurrence?</p><p className="text-muted-foreground">It keeps the first occurrence and removes subsequent duplicates.</p></div>
          <div><p className="font-semibold">3. Is the header row affected?</p><p className="text-muted-foreground">No, the first row is always treated as a header and is not checked for duplicates.</p></div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
